import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles2() {
  const { lesson, course, accent } = findLesson("the-beatles", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="The guitar solo" title="Triangulating chords by ear">
        <Prose>
          <p>
            The guitar solo of "Now and Then" is the section where Jason introduces the ear-training
            method he uses throughout all Beatles classes: <strong>triangulation</strong>. Rather
            than guessing a chord from a single source, you combine three separate streams —
            the guitar melody, the vocal harmony, and the bass — and let them confirm each other.
          </p>
          <p>
            At the start of the solo, the orchestra and voices are all sounding together. The first
            step is to isolate the bass. Whatever the bass plays at beat one of a bar is the root of
            the chord. "The bass will always confirm the final name of the chord. You may think it
            could be this or that, but the bass is playing the root. So we name chords primarily
            with respect to the bass."
          </p>
          <p>
            Working through the solo this way, the chord sequence reveals itself: D minor → C major
            → E minor → A minor. All four chords belong to A minor and its relative, C major —
            the same seven notes. G major is only one note away (F♯ instead of F), which is why
            the song can drift between these key areas so easily without really modulating.
          </p>
        </Prose>

        <TheoryBox label="The triangulation method — three sources, one chord">
          To find any unknown chord: (1) sing or play what the{" "}
          <strong>vocal harmony</strong> is doing — this gives you one or two chord tones. (2) Find
          what the <strong>guitar or melody</strong> is doing — this adds another note. (3) Find
          what the <strong>bass</strong> is playing at beat one — this names the chord. The bass is
          the final authority. Whatever the bass plays, that note becomes the chord root and the
          chord takes its name from there.
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="Opxhh9Oh3rg"
        title="Now and Then — The Beatles (2023)"
        caption="Skip to the guitar solo section. Listen for the bass line underneath — it stays on each root note clearly while the melody and orchestra move above it. Try to hear D, then C, then E minor, then A minor in the bass."
      />

      <Chapter eyebrow="The bass line" title="A melody that leads you to the next chord">
        <Prose>
          <p>
            McCartney's bass in "Now and Then" is not just a supporting instrument — it's a
            melody in its own right. At the end of each chord, just before the harmony changes,
            he plays a small run of notes that leads the ear toward the next chord. "The bass is
            almost guiding you to the next chord, in a sense."
          </p>
          <p>
            This is the difference between a player who simply sits on the root and one who has
            internalised the song deeply. On one A minor chord alone, McCartney plays close to
            twenty notes while the guitarist holds a single note for eight counts. "The question
            should always be who should get paid more." What he's doing, Jason explains, is
            "playing endless permutations, endless options" — like a player who has played close
            to a thousand basslines, so it happens instinctively.
          </p>
          <p>
            For students trying to follow this: use Moises to isolate just the bass track. You'll
            hear a distinct melodic shape at the end of each chord as the bass "presents" the
            next chord. When it lands on beat one of the new bar, it will always be the root of
            that chord.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="Guitar solo — A minor / C major / G major key area"
          caption="The guitar solo moves D minor → C major → E minor → A minor. All four chords belong to A minor / C major, which share the same seven notes. G major differs by one note (F♯), so the song can lean towards it at any moment."
          chords={[
            { chord: "Dm", rn: "iv", function: "Pre-dominant" },
            { chord: "C", rn: "♭III", function: "Tonic" },
            { chord: "Em", rn: "v", function: "Dominant" },
            { chord: "Am", rn: "i", function: "Tonic" },
          ]}
        />

        <MTQuote>
          "The bass player is making the song catchy and the bass player is leading you to the
          next chord, the inevitable chord, and the bass player is following the drummer of the
          band. What more should this guy do, is the question."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The D to D minor cliché" title="One of the most beautiful harmonic moves">
        <Prose>
          <p>
            Inside the guitar solo, there is a particular moment Jason identifies as "the D to
            D minor cliché — D to D major to D minor cliché." This is one of the most emotionally
            loaded moves in tonal harmony: a major chord shifting to its parallel minor on the
            same root. The note that changes is the third — from F♯ (major) to F♮ (minor). That
            single semitone drop carries an enormous weight.
          </p>
          <p>
            What makes this moment especially interesting is how the voices handle it. One of the
            singers slides between the two notes, following the chord change with a vocal glide.
            "Very Queen-like in nature," Jason says — "Lot of sliding vocals." The slide connects D major to
            D minor in a way that emphasises the emotional shift rather than hiding it.
          </p>
          <p>
            After D minor, the harmony moves to G, and you expect it to land on C major — D minor
            → G → C would be a 2-5-1 in C. It doesn't. It gets <em>deceived</em>, landing instead
            on A minor. "A is the six which is substituting the one which is C major. So there's
            substitution going on there." Jason also shows how the same chords read from G major's
            point of view: the solo sits between C major / A minor and G major.
          </p>
        </Prose>

        <TheoryBox label="D to D minor — the parallel minor shift">
          A major chord has a major third (the note four semitones above the root). The parallel
          minor chord on the same root has a minor third (three semitones above the root). Moving
          from D major to D minor means dropping just one note — F♯ falls to F♮. The ear hears
          this as a shadow passing over the same chord. It's the same root, the same fifth, but
          the third drops, and with it the emotional colour shifts from bright to dark. You hear
          it here in "Now and Then," and again in "In My Life" (D to D minor in A major).
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The ending" title="4/4 with a three-chord uneven cycle">
        <Prose>
          <p>
            "Now and Then" is unusual in having a carefully composed ending — not a fade-out,
            not a repeated chorus. "One of the few songs in this decade which focuses on the
            ending, if you think about it. Lot of songs in modern day musicians, the ending will
            just be a fade out. The same chorus will play, they fade it out and that's the ending.
            Here there's a proper ending."
          </p>
          <p>
            What makes the ending structurally strange is that the meter stays 4/4 throughout,
            but the chord cycle is three chords rather than four. "The meter is still 4/4, but
            the timing of the chords are uneven. It's not four chords in a cycle. It's three
            chords in a cycle." So the chord changes don't fall in the even, predictable places
            your ear expects from a 4/4 song.
          </p>
          <p>
            The outro begins abruptly at the word "you" — the chorus ends shortened, and then
            immediately this three-chord loop takes over. Jason notes that the Beatles do this a
            lot. To hear it clearly, use Moises and listen to only the ending section on repeat.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/02-now-and-then-pt2.png"
          caption="Now and Then Pt2 — guitar solo chord map and bass line sketch. The D to D minor cliché is marked at the transition point. The outro's three-chord cycle (in 4/4) is notated showing how the harmonic cycle and the metric cycle fall out of alignment."
        />

        <MTQuote>
          "Normally you'll do four by four with four chords or two chords. But here it's four
          by four with three chords."
        </MTQuote>
      </Chapter>
    </LessonShell>
  );
}
